import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Button, Dialog, FormSection, Icon, Menu, Spinner, formatDate } from "@omnabase/ui";
import { useSession } from "../../shell/store";
import { useNav, useSetParam, useWs } from "../../lib/nav";
import { GITHUB_SECTION, githubAccountUrl, githubInstallationUrl, goToGithubSignIn, integrationOf, integrations, whatStops, type GithubAccount, type GithubChoice, type GithubKind, type IntegrationDef } from "../../lib/integrations";

/** Settings › Integrations › GitHub (M5 round four; the GitHub accounts round, 2026-10-13; integrations round two, 2026-10-13; round three,
 *  2026-10-13: the GitHub tab, the section titled "GitHub" where it was "Code", and without the row that repeated the name): one "Connect a
 *  GitHub account" and the link to Knowledge base › Sources, then the table "GitHub accounts" (like the WordPress sites): the account linked to its page on GitHub,
 *  its kind, since when and by whom, its repository sources, and a row menu with "Its settings on GitHub" (the installation's settings page,
 *  a new tab) and Disconnect…. The line that the app stays installed on GitHub lives in the Disconnect dialog. Connect opens the scopes dialog
 *  (`?connect=github`), then the API's start route (GitHub's sign-in); GitHub's return lands here with `?pick=<handle>` and the chooser offers
 *  the person's accounts with the app (with none free, the API sends the browser straight on to GitHub's install page instead, 2026-10-13, so
 *  the chooser's empty text is only a fallback). Disconnect is `?disconnect=<installation id>`. The dialogs render whichever tab is shown
 *  (`shown` only draws the section): "Connect account" on Add new opens the same scopes dialog, and a pick selects the GitHub tab. */
export function GithubSettings({ accounts, shown, reload }: { accounts: GithubAccount[] | null; shown: boolean; reload: () => void }) {
  const [params] = useSearchParams(); const setParam = useSetParam(); const { toast } = useSession();
  const list = accounts ?? [];
  const connecting = integrationOf(params.get("connect") ?? "");
  const cutting = list.find((g) => g.installationId === params.get("disconnect"));
  const pick = params.get("pick");
  useEffect(() => { if (params.get("connected") === "github") { toast("GitHub connected · pick repositories under Knowledge base › Sources", { kind: "success" }); setParam("connected", null); } }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      {shown && <GithubSection accounts={list} />}
      {connecting?.id === "github" && <ConnectDialog integration={connecting} onClose={() => setParam("connect", null)} />}
      {cutting && <DisconnectDialog account={cutting} onClose={() => setParam("disconnect", null)} onDone={() => { setParam("disconnect", null); reload(); }} />}
      {pick && <ChooserDialog pick={pick} onClose={() => setParam("pick", null)} onDone={() => { setParam("pick", null); setParam("tab", "github"); reload(); }} />}
    </>
  );
}

const KIND: Record<GithubKind, string> = { organization: "Organization", user: "User" };
const kindLabel = (k: GithubKind | null) => (k ? KIND[k] : "Account");
/** the installation's settings page, once the kind is known (a row connected before kinds were kept learns it at the next sign-in) */
const settingsUrlOf = (g: GithubAccount) => (g.kind ? githubInstallationUrl({ ...g, kind: g.kind }) : null);

/** the GitHub tab: Connect another account and the link to where repositories are picked, then the connected accounts' table */
function GithubSection({ accounts }: { accounts: GithubAccount[] }) {
  const nav = useNav(); const { ws } = useWs(); const setParam = useSetParam();
  const kb = ws("/knowledge/sources");
  return (
    <FormSection title={GITHUB_SECTION.title} intro={GITHUB_SECTION.intro}>
      <div className="ob-github__actions">
        <Button icon="plus" onClick={() => setParam("connect", "github")}>Connect a GitHub account</Button>
        {accounts.length > 0 && <a className="ob-link" href={kb} onClick={(e) => nav(kb, e)}>Repositories are picked under Knowledge base › Sources</a>}
      </div>
      {accounts.length > 0 && (
        <div className="ob-dtable-card">
          <table className="ob-dtable ob-github-table" aria-label="GitHub accounts">
            <thead><tr><th>Account</th><th className="ob-dtable__col--below-420">Kind</th><th className="ob-dtable__col--below-680">Connected</th><th className="ob-dtable__col--below-540">Repositories</th><th></th></tr></thead>
            <tbody>{accounts.map((g) => <AccountRow key={g.installationId} account={g} onDisconnect={() => setParam("disconnect", g.installationId)} />)}</tbody>
          </table>
        </div>
      )}
    </FormSection>
  );
}

/** one connected account: its page on GitHub, its kind, who connected it and when, its repository sources, the menu */
function AccountRow({ account: g, onDisconnect }: { account: GithubAccount; onDisconnect: () => void }) {
  const { workspace } = useSession();
  const by = workspace.members.find((m) => m.userId === g.by)?.name ?? "someone";
  const settings = settingsUrlOf(g);
  return (
    <tr className="ob-dtable__row--static">
      <td className="ob-dtable__grow"><a className="ob-dtable__name ob-github-table__login" href={githubAccountUrl(g.login)} target="_blank" rel="noreferrer" title={g.login}>{g.login}</a></td>
      <td className="ob-dtable__muted ob-dtable__col--below-420">{g.kind ? KIND[g.kind] : "—"}</td>
      <td className="ob-dtable__muted ob-dtable__col--below-680">{formatDate(g.connectedAt)} · by {by}</td>
      <td className="ob-dtable__muted ob-dtable__col--below-540">{g.sources || "none"}</td>
      <td className="ob-dtable__actions">
        <Menu align="end" trigger={<Button variant="icon" icon="more" aria-label={`More for ${g.login}`} />} sections={[{ items: [
          ...(settings ? [{ id: "settings", label: "Its settings on GitHub", icon: "openInNew" as const, onSelect: () => window.open(settings, "_blank", "noopener,noreferrer") }] : []),
          { id: "disconnect", label: "Disconnect…", icon: "x", onSelect: onDisconnect },
        ] }]} />
      </td>
    </tr>
  );
}

/** what the connection reads and may change, then off to GitHub's sign-in (the start route's address with a fresh state; GitHub returns to the API, which sends the browser back to the chooser) */
function ConnectDialog({ integration: i, onClose }: { integration: IntegrationDef; onClose: () => void }) {
  const { slug } = useWs(); const { toast } = useSession();
  const [busy, setBusy] = useState(false); // stays busy on success: the browser is on its way to GitHub
  const go = () => { setBusy(true); goToGithubSignIn(slug).catch((e: Error) => { setBusy(false); toast(e.message, { kind: "error" }); }); };
  return (
    <Dialog title={`Connect ${i.name}`} intro={i.purpose} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={go}>Connect {i.name}</Button></>}>
      <div className="ob-scopes">
        <div className="ob-scopes__col"><span className="ob-scopes__title">It reads</span><ul>{i.reads.map((r) => <li key={r}><Icon name="check" size="sm" />{r}</li>)}</ul></div>
        <div className="ob-scopes__col"><span className="ob-scopes__title">It may change</span><ul>{i.writes.length ? i.writes.map((r) => <li key={r}><Icon name="pen" size="sm" />{r}</li>) : <li className="ob-scopes__none">Nothing</li>}</ul></div>
      </div>
      <p className="ob-dialog__hint">GitHub asks you to sign in; then you pick one of your accounts with the app here, or install the app on another one.</p>
    </Dialog>
  );
}

/** the chooser after GitHub's sign-in: the person's accounts with the app that this workspace has not connected; one pick per visit */
function ChooserDialog({ pick, onClose, onDone }: { pick: string; onClose: () => void; onDone: () => void }) {
  const { slug } = useWs(); const { toast } = useSession();
  const [data, setData] = useState<{ installations: GithubChoice[]; installUrl: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    integrations.githubChoices(slug, pick).then((r) => { if (live) setData(r); }, (e: Error) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [slug, pick]);
  const connect = async (c: GithubChoice) => {
    setBusy(c.installationId); setError(null);
    try { await integrations.pickGithub(slug, pick, c.installationId); toast(`${c.login} connected · pick repositories under Knowledge base › Sources`, { kind: "success" }); onDone(); }
    catch (e) { setError((e as Error).message); setBusy(null); }
  };
  return (
    <Dialog title="Choose a GitHub account" intro="Your GitHub accounts and organizations with the OmnaBase app. Connect one; for another, connect again." onClose={onClose}
      footer={<><span className="ob-dialog__foot-meta">{data ? error ?? "" : ""}</span>{data && <a className="ob-btn ob-btn--secondary" href={data.installUrl}>Install on another account</a>}<Button onClick={onClose}>Cancel</Button></>}>
      {!data ? (error ? <p className="ob-dialog__hint">{error}</p> : <p className="ob-dialog__hint ob-dialog__loading"><Spinner /> Reading your GitHub accounts…</p>)
        : data.installations.length === 0 ? <p className="ob-dialog__hint">None of your GitHub accounts has the app and is free for this workspace. Install it on another account.</p> : (
          <ul className="ob-checks" aria-label="Your GitHub accounts with the app">{data.installations.map((c) => (
            <li key={c.installationId}>
              <span><strong>{c.login}</strong> · {kindLabel(c.kind)}</span>
              {c.takenBy ? <span className="ob-checks__note">Used by another workspace · ask {c.takenBy}</span>
                : <Button size="sm" variant="primary" aria-label={`Connect ${c.login}`} busy={busy === c.installationId} disabled={!!busy} onClick={() => void connect(c)}>Connect</Button>}
            </li>
          ))}</ul>
        )}
    </Dialog>
  );
}

/** Disconnect one account: what stops, counted from that account's repository sources, and that the app stays installed on GitHub (with
 *  the link to the installation's settings there, once the kind is known) */
function DisconnectDialog({ account: g, onClose, onDone }: { account: GithubAccount; onClose: () => void; onDone: () => void }) {
  const { slug } = useWs(); const { toast } = useSession();
  const [busy, setBusy] = useState(false);
  const stops = whatStops({ repositories: g.sources, codeProjects: g.projects }); // a connected repository always serves investigations (the cleanup round, 2026-10-12)
  const settings = settingsUrlOf(g);
  const cut = async () => {
    setBusy(true);
    try { await integrations.disconnectGithub(slug, g.installationId); toast(`${g.login} disconnected`, { kind: "success" }); onDone(); } catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(false); }
  };
  return (
    <Dialog title={`Disconnect ${g.login}?`} intro={`${g.login}. It can be connected again at any time.`} onClose={onClose}
      footer={<><Button onClick={onClose}>Keep it connected</Button><Button variant="primary" busy={busy} onClick={() => void cut()}>Disconnect</Button></>}>
      <ul className="ob-consequences">{stops.map((t) => <li key={t}><Icon name="alarm" size="sm" />{t}</li>)}</ul>
      <p className="ob-dialog__hint">Disconnect only stops reading here: the OmnaBase app stays installed on GitHub. {settings && <a className="ob-link" href={settings} target="_blank" rel="noreferrer">Its settings on GitHub</a>}</p>
    </Dialog>
  );
}
